import type { Map as MapLibreMap } from 'maplibre-gl';
import { signedCameraOrientation } from './signed-camera-orientation';

const eventName = 'overland-traces:preview-rotation';

/** Preview-only camera input; exported frames continue through the full renderer. */
export function requestPreviewRotation(document: Document, rotation: number | undefined): boolean {
  const EventType = document.defaultView?.CustomEvent;
  if (!EventType) return false;
  const event = new EventType(eventName, { detail: { rotation }, cancelable: true });
  document.dispatchEvent(event);
  return event.defaultPrevented;
}

/** Keep the newest drag visible even if an older asynchronous frame finishes. */
export function observePreviewRotation(map: MapLibreMap, document: Document): () => void {
  let rotation: number | undefined;
  const apply = () => {
    if (rotation === undefined) return;
    const data = map.getCanvas().dataset;
    const tilt = Number(data.cameraRequestedTilt ?? map.getPitch());
    const bearing = signedCameraOrientation(rotation, tilt, 0).bearing;
    const difference = ((map.getBearing() - bearing + 540) % 360 + 360) % 360 - 180;
    if (Math.abs(difference) > .001) map.jumpTo({ bearing });
    data.cameraRotation = String(rotation);
  };
  const receive = (event: Event) => {
    const value: unknown = (event as CustomEvent<{ rotation?: unknown }>).detail?.rotation;
    if (value !== undefined && (typeof value !== 'number' || !Number.isFinite(value))) return;
    rotation = value as number | undefined;
    event.preventDefault();
    apply();
  };
  document.addEventListener(eventName, receive);
  map.on('render', apply);
  return () => { rotation = undefined; document.removeEventListener(eventName, receive); map.off('render', apply); };
}
